import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

const API_URL = `${API_BASE_URL}/sharepoint-webhook`;

export interface WebhookSubscription {
  subscriptionId: string;
  siteId: string;
  driveId: string;
  libraryName?: string;
  resource: string;
  expirationDateTime: string;
  createdAt: string;
  lastRenewed?: string;
}

/**
 * SharePoint Webhook Service
 * Manages webhook subscriptions for real-time updates
 */
class SharePointWebhookService {
  /**
   * Subscribe to a SharePoint library for real-time updates
   */
  async subscribe(siteId: string, driveId: string, libraryName?: string): Promise<WebhookSubscription> {
    try {
      console.log('[SharePointWebhookService] Subscribing to library:', driveId);

      const response = await axios.post(`${API_URL}/subscribe`, {
        siteId,
        driveId,
        libraryName,
      });

      return response.data.data;
    } catch (error: any) {
      console.error('[SharePointWebhookService] Error subscribing:', error);
      throw new Error(error.response?.data?.error || 'Failed to subscribe to library');
    }
  }

  /**
   * Unsubscribe from a library
   */
  async unsubscribe(subscriptionId: string): Promise<void> {
    try {
      console.log('[SharePointWebhookService] Unsubscribing:', subscriptionId);

      await axios.delete(`${API_URL}/unsubscribe/${subscriptionId}`);
    } catch (error: any) {
      console.error('[SharePointWebhookService] Error unsubscribing:', error);
      throw new Error(error.response?.data?.error || 'Failed to unsubscribe');
    }
  }

  /**
   * Get all active subscriptions
   */
  async getSubscriptions(): Promise<WebhookSubscription[]> {
    try {
      const response = await axios.get(`${API_URL}/subscriptions`);
      return response.data.data || [];
    } catch (error: any) {
      console.error('[SharePointWebhookService] Error fetching subscriptions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch subscriptions');
    }
  }

  /**
   * Renew a subscription
   */
  async renewSubscription(subscriptionId: string): Promise<WebhookSubscription> {
    try {
      console.log('[SharePointWebhookService] Renewing subscription:', subscriptionId);

      const response = await axios.post(`${API_URL}/renew/${subscriptionId}`);
      return response.data.data;
    } catch (error: any) {
      console.error('[SharePointWebhookService] Error renewing subscription:', error);
      throw new Error(error.response?.data?.error || 'Failed to renew subscription');
    }
  }

  /**
   * Check if a library is subscribed
   */
  async isSubscribed(driveId: string): Promise<boolean> {
    try {
      const subscriptions = await this.getSubscriptions();
      return subscriptions.some(sub => sub.driveId === driveId);
    } catch (error) {
      console.error('[SharePointWebhookService] Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Get subscription for a specific library
   */
  async getLibrarySubscription(driveId: string): Promise<WebhookSubscription | null> {
    try {
      const subscriptions = await this.getSubscriptions();
      return subscriptions.find(sub => sub.driveId === driveId) || null;
    } catch (error) {
      console.error('[SharePointWebhookService] Error getting library subscription:', error);
      return null;
    }
  }
}

export default new SharePointWebhookService();
